import React, { useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';

import { Backdrop, Tappable } from '@common/ui/general';
import { useMediaQuery } from '@common/hooks';

import analytics from '~chatbot/analytics';
import ChatbotHeader from '~chatbot/ui/ChatbotHeader';
import ChatbotErrorWindow from '~chatbot/ui/ChatbotErrorWindow';
import ChatbotModal from '~chatbot/ui/ChatbotModal';
import { useChatbotContext } from '~chatbot/context/ChatbotContext';

import submitIcon from '~chatbot/assets/submit-icon.svg';
import strokeIcon from '~chatbot/assets/stroke-icon.svg';
import chatbotImage from '~chatbot/assets/chatbot-character.webp';
import { SLUG_TO_CONFIG } from '~chatbot/config';

import styles from './ChatbotWidget.module.scss';

function ChatbotWidget({
  header, logo, extraClass, errorNotice, initialNote,
}) {
  const { mobile } = useMediaQuery();
  const [showNudge, setShowNudge] = useState(false);

  const {
    openWidget, prompt, setPrompt, chatbotAccess,
    handleAction, handleSubmit, chatbotSlug, config,
    isFetching,
  } = useChatbotContext();

  const { placeholderText, productName, disableNudge } = config;


  const icon = openWidget ? submitIcon : chatbotImage;
  const isScalerProduct = (
    SLUG_TO_CONFIG['scaler-chatbot'].productName === productName
  );


  const handleTracking = (properties, ctaType = 'cta_click', source) => {
    analytics.click(
      ctaType,
      source,
      properties,
    );
  };

  const keyPressed = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !isFetching) {
      e.preventDefault();
      handleSubmit();
      handleTracking({
        click_type: 'enter_key_press',
        click_text: '',
        element: 'prompt input box',
      });
    }
  };

  const handleChatbotBtn = () => {
    handleAction();
    handleTracking({
      click_type: 'button_click',
      click_text: '',
      element: openWidget ? 'Send Icon' : 'Chatbot Icon',
    });
  };

  const handleTimeout = useCallback(() => {
    if (disableNudge) return;
    if (!openWidget) {
      setShowNudge(true);
    }
  }, [openWidget, disableNudge]);

  const handleClick = useCallback(() => {
    setShowNudge(false);
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(handleTimeout, 10000);

    window.addEventListener('click', handleClick);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('click', handleClick);
    };
  }, [handleClick, handleTimeout, openWidget]);

  return (
    <div className={classNames(styles.chatbot,
      { [extraClass]: !!extraClass && !openWidget })}
    >
      <Backdrop
        isOpen={openWidget && mobile}
        canClose={false}
        className={styles.backdrop}
      />
      <div className={classNames(
        styles.container,
        { [styles.hide]: !openWidget },
      )}
      >
        <ChatbotHeader
          header={header}
          logo={logo}
          extraClass={extraClass}
          handleTracking={handleTracking}
        />
        { chatbotAccess
          ? (
            <ChatbotModal
              chatbotSlug={chatbotSlug}
              extraClass={extraClass}
              initialNote={initialNote}
            />
          )
          : (
            <ChatbotErrorWindow
              extraClass={extraClass}
              errorNotice={errorNotice}
            />
          )}
        <form
          className={classNames(
            styles.message,
            { [styles.disabled]: !chatbotAccess },
            { [styles.extraClass]: !!extraClass },
          )}
          onSubmit={handleSubmit}
        >
          <textarea
            placeholder={placeholderText}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={(e) => keyPressed(e)}
          />
        </form>
      </div>

      {!disableNudge && showNudge && !openWidget && (
        <div className={classNames(
          styles.popup,
          { [styles.smallV]: isScalerProduct },
        )}
        >
          Hi, I'm Aditi...
        </div>
      )}

      {!showNudge && !openWidget && (
        <div className={styles.stroke}>
          <img
            src={strokeIcon}
            alt="stroke"
          />
        </div>
      )}

      <Tappable
        className={classNames(
          styles.logo,
          { [styles.btn]: openWidget },
          {
            [styles.disabled]: openWidget
            && (!chatbotAccess || (!prompt) || isFetching),
          },
          styles.logoV2,
          { [styles.btnV2]: openWidget },
          { [styles.smallBtn]: isScalerProduct },
        )}
        onClick={handleChatbotBtn}
      >
        <img
          src={icon}
          alt="chat"
          className={classNames(
            { [styles.image]: !openWidget },
          )}
        />
      </Tappable>
    </div>
  );
}

export default ChatbotWidget;
